/**
 * HL-12516 — Flowzz attribution on the funnel page views (start-consultation → payment).
 * The Flowzz params (af, fl_uid, p1..p3, oos) are only on the landing URL, so they are captured
 * when the visitor lands on start-consultation and replayed from sessionStorage on every later page
 * (survives refresh and back navigation within the tab).
 */

import { FLOWZZ, FLOWZZ_PREMIUM } from "./consts";

type YesNo = "Yes" | "No";

export type FlowzzTrackingProps = {
  "Came from Flowzz?": YesNo;
  "Flowzz Premium?": YesNo;
  "Flowzz Product Out of Stock?": YesNo;
  "Flowzz User ID"?: string;
  p1?: string;
  p2?: string;
  p3?: string;
};

const STORAGE_KEY = "flowzz_tracking_props";
const PRODUCT_SLOTS = ["p1", "p2", "p3"] as const;

export const buildFlowzzTrackingProps = (search: string): FlowzzTrackingProps => {
  const params = new URLSearchParams(search);
  const af = params.get("af");
  const cameFromFlowzz = af === FLOWZZ || af === FLOWZZ_PREMIUM;
  // Flowzz marks the out-of-stock product slots in `oos` (e.g. oos=p1,p3)
  const oosSlots = new Set<string>((params.get("oos") || "").split(","));
  const isProductOutOfStock = cameFromFlowzz && PRODUCT_SLOTS.some((slot) => oosSlots.has(slot) && Boolean(params.get(slot)));
  const flowzzUserID = cameFromFlowzz ? params.get("fl_uid") : null;
  // The linked Flowzz products, only the slots present on the landing URL
  const products: Partial<Record<typeof PRODUCT_SLOTS[number], string>> = {};
  PRODUCT_SLOTS.forEach((slot) => {
    const product = params.get(slot);
    if (cameFromFlowzz && product) products[slot] = product;
  });
  return {
    "Came from Flowzz?": cameFromFlowzz ? "Yes" : "No",
    "Flowzz Premium?": af === FLOWZZ_PREMIUM ? "Yes" : "No",
    "Flowzz Product Out of Stock?": isProductOutOfStock ? "Yes" : "No",
    ...(flowzzUserID ? { "Flowzz User ID": flowzzUserID } : {}),
    ...products,
  };
};

const readStoredProps = (): FlowzzTrackingProps | null => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "null");
    return stored?.["Came from Flowzz?"] ? (stored as FlowzzTrackingProps) : null;
  } catch {
    return null;
  }
};

/** Landing page: (re)capture the Flowzz props from the current URL. */
export const captureFlowzzTrackingProps = (): FlowzzTrackingProps => {
  const props = buildFlowzzTrackingProps(window.location.search);
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(props));
  } catch {
    // storage blocked: later pages fall back to their own URL
  }
  return props;
};

/** Later funnel pages: the props captured on landing (or the current URL when nothing was captured). */
export const getFlowzzTrackingProps = (): FlowzzTrackingProps => readStoredProps() ?? captureFlowzzTrackingProps();
